/**
 * Inventory-only helpers from SCAPI `product.inventory` (FSD PDP Simple).
 * No PDP layout / stock row UI — use {@link getPdpStockIndicator} in `pdp-stock-utils.js` for that.
 *
 * IM-004: When `getProduct(s)` is called with `inventoryIds`, SCAPI may return multiple inventory
 * records; use {@link pickInventoryRecordForListIds} via the optional third argument to `getInventory`.
 */

import {isPerpetualVariant} from './cover-stock-display';

/**
 * @param {Object|null|undefined} source - Product or variant
 * @returns {Array|null}
 */
function listInventoryRecords(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const arr = source.inventories ?? source.inventoryRecords;

  return Array.isArray(arr) && arr.length > 0 ? arr : null;
}

/**
 * @param {Object} record - Single SCAPI inventory record
 * @returns {Object}
 */
function normalizeInventoryRecordShape(record) {
  if (!record || typeof record !== 'object') {
    return record;
  }

  const ats = record.ats ?? record.stockLevel ?? record.availableToSell ?? 0;

  return {
    ...record,
    ats,
    stockLevel: record.stockLevel ?? ats,
  };
}

/**
 * Pick the inventory record matching warehouse list ids when multiple are returned (IM-004).
 *
 * @param {Object|null|undefined} inventorySource - Product or variant carrying inventory fields
 * @param {string|undefined|null} inventoryIdsCsv - Same formatted string passed to SCAPI `inventoryIds`
 * @returns {Object|null}
 */
export function pickInventoryRecordForListIds(inventorySource, inventoryIdsCsv) {
  const ids =
    inventoryIdsCsv ?
      inventoryIdsCsv.split(',').map((s) => s.trim()).filter(Boolean) :
      [];

  const records = listInventoryRecords(inventorySource);

  if (!records || ids.length === 0) {
    return null;
  }

  for (const id of ids) {
    const match = records.find((r) => r && (
      r.inventoryListId === id ||
      r.id === id ||
      r.inventoryId === id ||
      String(r.inventoryListId) === String(id)
    ));

    if (match) {
      return normalizeInventoryRecordShape(match);
    }
  }

  return null;
}

/**
 * @enum {string}
 */
export const INVENTORY_STATUS = Object.freeze({
  IN_STOCK: 'in-stock',
  LOW_STOCK: 'low-stock',
  OUT_OF_STOCK: 'out-of-stock',
  UNKNOWN: 'unknown',
});

/**
 * Inventory checks from `product.inventory` (KINDW-1127).
 *
 * Status is determined by `orderable` + `ats` only:
 * - `!orderable || ats <= 0` → `OUT_OF_STOCK`
 * - `orderable && ats >= 1 && ats <= 4` → `LOW_STOCK`
 * - `orderable && ats > 4` → `IN_STOCK`
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} variant - Optional variant to check instead of product
 * @param {string|undefined|null} [inventoryIdsFromCookie] - Formatted `inventoryIds` sent to SCAPI (IM-004)
 * @returns {{
 *   status: string,
 *   canAddToCart: boolean,
 *   showNotifyMe: boolean
 * }}
 */
export const getInventory = (product, variant, inventoryIdsFromCookie) => {
  const empty = {
    status: INVENTORY_STATUS.UNKNOWN,
    canAddToCart: false,
    showNotifyMe: false,
  };

  // Supports two variant shapes:
  // 1. Standard SCAPI shape: variant.inventory sub-object
  // 2. c_allVariantsData shape: direct ats/orderable top-level fields
  const variantHasDirectFields = variant && (variant.ats !== undefined || variant.orderable !== undefined);
  const inventorySource = (variant?.inventory || variantHasDirectFields) ? variant : product;

  const picked = pickInventoryRecordForListIds(inventorySource, inventoryIdsFromCookie);
  let inventory = picked ?? inventorySource?.inventory;

  // c_allVariantsData variant: normalize direct fields into an inventory-shaped object
  if (!inventory && variantHasDirectFields) {
    inventory = {ats: variant.ats, stockLevel: variant.stockLevel ?? variant.ats, orderable: variant.orderable};
  }

  if (!inventory) {
    return empty;
  }

  const orderable = inventory.orderable === true;

  if (!orderable) {
    return {status: INVENTORY_STATUS.OUT_OF_STOCK, canAddToCart: false, showNotifyMe: true};
  }

  const inventoryEntity = variant ?? product;

  // Made-to-order / perpetual variants (incl. c_allVariantsData with ats:0 + perpetual:true).
  if (isPerpetualVariant(inventoryEntity) || isMtoOrderableProduct(inventoryEntity)) {
    return {status: INVENTORY_STATUS.IN_STOCK, canAddToCart: true, showNotifyMe: false};
  }

  // atsRaw is null/undefined when the variant has orderable:true but no ATS field.
  // In that case, treat as IN_STOCK (do not fall back to 0).
  const atsRaw = inventory.ats ?? inventory.stockLevel;

  if (atsRaw != null && atsRaw <= 0) {
    return {status: INVENTORY_STATUS.OUT_OF_STOCK, canAddToCart: false, showNotifyMe: true};
  }

  if (atsRaw != null && atsRaw <= 4) {
    return {status: INVENTORY_STATUS.LOW_STOCK, canAddToCart: true, showNotifyMe: false};
  }

  return {status: INVENTORY_STATUS.IN_STOCK, canAddToCart: true, showNotifyMe: false};
};

/**
 * Resolve the raw ATS / stockLevel value for a product without coupling to the
 * `IN_STOCK / LOW_STOCK / OUT_OF_STOCK` semantics of {@link getInventory}.
 *
 * Used by callers that need to look at the bare ATS number (e.g. the Payment
 * Amount visibility logic, which treats the SFCC sentinel `999999` as
 * "orderable but no on-hand inventory" rather than "in stock"). When multiple
 * inventory records are returned (IM-004) we pick the warehouse record that
 * matches `inventoryIdsFromCookie`; otherwise we fall back to the default
 * `product.inventory`.
 *
 * Returns `null` when no inventory record is present so callers can
 * distinguish "no data" from `ats === 0`.
 *
 * @param {Object|null|undefined} product - SCAPI product or variant
 * @param {string|undefined|null} [inventoryIdsFromCookie] - Formatted `inventoryIds` sent to SCAPI (IM-004)
 * @returns {number|null}
 */
export const getProductAts = (product, inventoryIdsFromCookie) => {
  if (!product) return null;

  const picked = pickInventoryRecordForListIds(product, inventoryIdsFromCookie);
  const inventory = picked ?? product?.inventory;

  if (!inventory) return null;

  const ats = inventory.ats ?? inventory.stockLevel;

  return typeof ats === 'number' ? ats : null;
};

/**
 * Resolve the raw ATS for a single product/variant entity, tolerating BOTH
 * inventory shapes {@link getInventory} understands:
 *   1. the SCAPI `.inventory` (or IM-004 `.inventories`) record, and
 *   2. the `c_allVariantsData` direct-fields shape (top-level `ats` / `orderable`).
 *
 * Returns the bare ATS number (or `null` when unknown) so callers can apply
 * their own thresholds — e.g. the deposit "in-stock" rule that treats the
 * `999999` sentinel as NOT in stock. Operates on the passed entity ONLY (no
 * master fallback): deposit eligibility checks the exact entity the shopper has
 * active (the selected variant for a master-variant product, otherwise the
 * product), mirroring the checkout Payment Amount rule which evaluates the
 * basket line's own product.
 *
 * @param {Object|null|undefined} entity - SCAPI product/variant or `c_allVariantsData` entry
 * @param {string|undefined|null} [inventoryIdsFromCookie] - Formatted `inventoryIds` sent to SCAPI (IM-004)
 * @returns {number|null}
 */
export const resolveEntityAts = (entity, inventoryIdsFromCookie) => {
  if (!entity) return null;

  const hasDirectFields = entity.ats !== undefined || entity.orderable !== undefined;

  if (hasDirectFields && !entity.inventory) {
    const directAts = entity.ats ?? entity.stockLevel;

    return typeof directAts === 'number' ? directAts : null;
  }

  return getProductAts(entity, inventoryIdsFromCookie);
};

/**
 * MTO (made-to-order): orderable with zero ATS — still addable; not Notify Me OOS.
 *
 * @param {Object|null|undefined} productOrVariant - SCAPI product or `c_allVariantsData` entry
 * @returns {boolean}
 */
export function isMtoOrderableProduct(productOrVariant) {
  if (!productOrVariant) return false;

  if (isPerpetualVariant(productOrVariant)) {
    return true;
  }

  const inv = productOrVariant.inventory ?? {};
  const ats = productOrVariant.ats ?? inv.ats;
  const orderable = productOrVariant.orderable ?? inv.orderable;

  return orderable === true && ats === 0;
}

/**
 * True when the SKU should show Notify Me / block customize ATC (excludes MTO).
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} variant
 * @param {string|undefined|null} [inventoryIdsFromCookie]
 * @returns {boolean}
 */
export function shouldShowNotifyMeForProduct(product, variant, inventoryIdsFromCookie) {
  if (variant) {
    if (isMtoOrderableProduct(variant)) {
      return false;
    }

    const variantInventory = getInventory(null, variant, inventoryIdsFromCookie);

    if (variantInventory.showNotifyMe) {
      return true;
    }
  }

  if (isMtoOrderableProduct(product)) {
    return false;
  }

  return getInventory(product, variant, inventoryIdsFromCookie).showNotifyMe;
}
